// src/data/contact.js

import generateId from 'utils/helpers';

/**
 * @typedef {Object} ContactItem
 * @property {number} id - A unique, auto-incrementing identifier for the contact item.
 * @property {string} name - The name of the platform (e.g., 'LinkedIn', 'GitHub').
 * @property {string} icon - The string key for the icon (e.g., 'LinkedIn', 'Github').
 * @property {string} type - The type of contact (e.g., 'social', 'email', 'phone').
 * @property {string} url - The full URL or protocol for the contact method (e.g., 'https://...', 'mailto:...').
 * @property {string} display - The text to be displayed for the link (e.g., '@BonaventureCJ', 'bonacjugwu@gmail.com').
 * @property {string} label - A descriptive label for accessibility.
 * @property {string} ariaLabel - A more explicit, hidden label for screen readers.
 * @property {boolean} isFeatured - Indicates if the link should be featured on the homepage.
 * @property {string} className - The CSS class for styling.
 */

/** @type {ContactItem[]} */
const contactItems = [
  // Social Links
  {
    id: generateId(),
    name: 'LinkedIn',
    icon: 'LinkedInIcon',
    type: 'social',
    url: 'https://www.linkedin.com/in/bonaventure-ugwu',
    display: 'Bonaventure Ugwu',
    label: 'Bonaventure Ugwu on LinkedIn',
    ariaLabel: 'Visit Bonaventure Ugwu on LinkedIn',
    isFeatured: true,
  },
  {
    id: generateId(),
    name: 'GitHub',
    icon: 'GithubIcon',
    type: 'social',
    url: 'https://github.com/BonaventureCJ',
    display: 'BonaventureCJ',
    label: 'Bonaventure Ugwu on GitHub',
    ariaLabel: "Visit my GitHub profile",
    isFeatured: true,
  },
  {
    id: generateId(),
    name: 'X',
    icon: 'XIcon',
    type: 'social',
    url: 'https://x.com/BonaCJUgwu',
    display: '@BonaCJUgwu',
    label: 'Bonaventure Ugwu on X',
    ariaLabel: "Follow Bonaventure Ugwu on X",
    isFeatured: false,
  },
  // Communication Links
  {
    id: generateId(),
    name: 'Email',
    icon: 'EmailIcon',
    type: 'email',
    url: 'mailto:bonacjugwu@gmail.com',
    display: 'bonacjugwu@gmail.com',
    label: 'Send an Email to Bonaventure',
    ariaLabel: "Send an email to bonacjugwu@gmail.com",
    isFeatured: true,
  },
  {
    id: generateId(),
    name: 'WhatsApp',
    icon: 'WhatsappIcon',
    type: 'whatsapp',
    url: 'https://wa.me/+2348061103300',
    display: '+234 806 110 3300',
    label: 'Chat Bonaventure on WhatsApp',
    ariaLabel: "Chat with Bonaventure on WhatsApp",
    isFeatured: false,
  },
  {
    id: generateId(),
    name: 'Phone/SMS',
    icon: 'SmsIcon',
    type: 'phone',
    url: 'sms:+2348061103300',
    display: '+234 806 110 3300',
    label: 'Send Bonaventure an SMS',
    ariaLabel: "Send an SMS to Bonaventure at +234 806 110 3300",
    isFeatured: false,
  },
].map(item => ({
  ...item,
  className: `${item.name.toLowerCase()}-icon`
}));

// Combine the two data messages into one object or keep it separate based on preference
const ctaMessage = {
  form: "You can reach out to me using the form below. I'd love to hear from you!",
  general: "Let's connect and build something great together. Find me on social media or reach me directly.",
};

export { contactItems, ctaMessage };

/* ### Each contactItem receives a className property via map; details below:
*   **`.map(item => ({ ...item, className: `${item.name.toLowerCase()}-icon` }))`:** This code iterates over each `item` in the `contactItems` array.
*   **`{ ...item, ... }`:** The spread syntax is used to create a new object that includes all the properties of the original `item`.
*   **`className: `${item.name.toLowerCase()}-icon``:** A new `className` property is added to each object. The value is dynamically generated by converting the `name` property to lowercase and appending `"-icon"`. For example, the LinkedIn item will get `className: "linkedin-icon"`.
*   **Updated JSDoc:** The `@typedef` comment for `ContactItem` is updated to reflect the new `className` property, ensuring clear documentation.
 */